/* eslint-disable no-nested-ternary */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { DueListRoutes } from 'src/app/core/enums/routes.enum';
import { ToastService } from 'src/app/core/services/toast.service';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { IdName } from 'src/app/shared/models/id-name';
import { ISparePartViewModel } from 'src/app/storage-user/models/product.model';
import { ActivityStatus } from 'src/app/shared/enums/activity-status.enum';
import { DueListState } from 'src/app/due-list/state/due-list.state';
import { ClearLastDueListItem } from 'src/app/due-list/state/due-list.actions';
import {
  getLinkToCorrectiveActionEdit,
  getLinkToMaintenanceJobDetails,
} from '../functions/route-helpers';
import { getMaintenanceComponentReturnTarget } from '../functions/get-maintenance-component-return-target';
import { mapCorrectiveActionToMaintenanceJob } from '../functions/map-corrective-action-to-maintenance-job';
import {
  IApiResponseGroupMaintenanceJobs,
  IMaintenanceJob,
  IMaintenanceJobGroupData,
  IMaintenanceJobLogEntry,
} from '../models/maintenance-job.model';
import { IMaintenanceJobStatusResponse } from '../models/maintenance-job-status-response.model';
import { IBulkPerformJobsResponse, IPerformJobDetails } from '../models/perform-job-request.model';
import { CurrentComponentIdService } from '../services/current-component-id.service';
import { CorrectiveActionApiService } from '../services/corrective-action-api.service';
import { MaintenanceJobApiService } from '../services/maintenance-job-api.service';
import {
  BulkPerformMaintenanceJobs,
  ClearMaintenanceJobsByMenu,
  CreateMaintenanceJob,
  DeactivateMaintenanceJob,
  DeleteMaintenanceJob,
  GetCorrectiveActionPerformDetails,
  GetPerformMaintenanceJobDetails,
  LoadCorrectiveActionById,
  LoadCorrectiveActionPerformById,
  LoadJobDraftPerform,
  LoadMaintenanceJobById,
  LoadMaintenanceJobLog,
  LoadRecentMaintenanceJobPerformLogs,
  LoadMaintenanceJobs,
  LoadMaintenanceJobVersionById,
  LoadMaintenanceJobsForIO,
  LoadMaintenanceJobStatus,
  LoadSparePartsForPerform,
  PerformMaintenanceJob,
  PostponeMaintenanceJob,
  PrintMaintenanceJobs,
  PrintMaintenanceJobsReport,
  ReactivateMaintenanceJob,
  RestoreMaintenanceJob,
  SaveJobDraftPerform,
  UpdateMaintenanceJob,
  ClearCurrentMaintenanceJob,
} from './maintenance-job.actions';
import { IPerformGroupedSpareParts } from '../models/perform-grouped-spare-parts.model';

export interface MaintenanceJobStateModel {
  groupedJobs: IMaintenanceJobGroupData[] | undefined;
  groupedDeletedJobs: IMaintenanceJobGroupData[] | undefined;
  groupedDeactivatedJobs: IMaintenanceJobGroupData[] | undefined;
  groupedCompletedJobs: IMaintenanceJobGroupData[] | undefined;
  maintenanceJobsTotalPages: number;
  deletedJobsTotalPages: number;
  deactivatedJobsTotalPages: number;
  completedJobsTotalPages: number;
  currentMaintenanceJob: IMaintenanceJob | undefined;
  currentMaintenanceJobVersion: IMaintenanceJob | undefined;
  currentPerformDetails: IPerformJobDetails | undefined;
  currentMaintenanceJobLog: IMaintenanceJobLogEntry[] | undefined;
  recentPerformLogs: IMaintenanceJobLogEntry[] | undefined;
  currentPrintedJobs: Blob | undefined;
  draftPerform: IPerformJobDetails | undefined;
  lastPerformId: string | undefined;
  lastBulkPerformResult: IBulkPerformJobsResponse | undefined;
  jobsForIO: IdName[];
  componentSpareParts: ISparePartViewModel[];
  maintenanceJobSpareParts: ISparePartViewModel[];
  storageSpareParts: ISparePartViewModel[];
  maintenanceJobsReport: Blob;
  maintenanceJobStatus: IMaintenanceJobStatusResponse | undefined;
}

const MAINTENANCE_JOB_STATE_TOKEN = new StateToken<MaintenanceJobStateModel>('maintenanceJob');

@State<MaintenanceJobStateModel>({
  name: MAINTENANCE_JOB_STATE_TOKEN,
  defaults: {
    groupedJobs: undefined,
    groupedDeletedJobs: undefined,
    groupedDeactivatedJobs: undefined,
    groupedCompletedJobs: undefined,
    maintenanceJobsTotalPages: 0,
    deletedJobsTotalPages: 0,
    deactivatedJobsTotalPages: 0,
    completedJobsTotalPages: 0,
    currentMaintenanceJob: undefined,
    currentMaintenanceJobVersion: undefined,
    currentPerformDetails: undefined,
    currentMaintenanceJobLog: undefined,
    recentPerformLogs: undefined,
    currentPrintedJobs: undefined,
    draftPerform: undefined,
    lastPerformId: undefined,
    lastBulkPerformResult: undefined,
    jobsForIO: [],
    componentSpareParts: [],
    maintenanceJobSpareParts: [],
    storageSpareParts: [],
    maintenanceJobsReport: <Blob>{},
    maintenanceJobStatus: undefined,
  },
})
@Injectable()
export class MaintenanceJobState {
  private get currentComponentId(): string {
    return this.componentIdService.get();
  }

  constructor(
    private service: MaintenanceJobApiService,
    private correctiveActionApiService: CorrectiveActionApiService,
    private componentIdService: CurrentComponentIdService,
    private store: Store,
    private toast: ToastService,
    private router: Router
  ) {}

  @Action(LoadMaintenanceJobs, { cancelUncompleted: false })
  loadMaintenanceJobs(
    { getState, setState }: StateContext<MaintenanceJobStateModel>,
    { filter }: LoadMaintenanceJobs
  ) {
    return this.service.loadMaintenanceJobs(filter).pipe(
      tap((response: IApiResponseGroupMaintenanceJobs) => {
        const state = getState();
        const jobsKeyMap: Record<string, keyof MaintenanceJobStateModel> = {
          [ActivityStatus.Deleted]: 'groupedDeletedJobs',
          [ActivityStatus.Deactivated]: 'groupedDeactivatedJobs',
          [ActivityStatus.Completed]: 'groupedCompletedJobs',
        };
        const totalPagesKeyMap: Record<string, keyof MaintenanceJobStateModel> = {
          [ActivityStatus.Deleted]: 'deletedJobsTotalPages',
          [ActivityStatus.Deactivated]: 'deactivatedJobsTotalPages',
          [ActivityStatus.Completed]: 'completedJobsTotalPages',
        };
        const status = filter?.additional?.activityStatus;
        const jobs = jobsKeyMap[status] ?? 'groupedJobs';
        const totalPages = totalPagesKeyMap[status] ?? 'maintenanceJobsTotalPages';
        setState({
          ...state,
          [jobs]: response.groupData,
          [totalPages]: response.totalPages,
        });
      })
    );
  }

  @Action(LoadSparePartsForPerform, { cancelUncompleted: true })
  loadSparePartsForPerform(
    { getState, setState }: StateContext<MaintenanceJobStateModel>,
    { id, isCorrectiveAction }: LoadSparePartsForPerform
  ) {
    const sparePartsRequest = isCorrectiveAction
      ? this.correctiveActionApiService.loadSparePartsForPerform(id)
      : this.service.loadMaintenanceJobsForPerform(id);

    return sparePartsRequest.pipe(
      tap((spareParts: IPerformGroupedSpareParts) => {
        const state = getState();
        setState({
          ...state,
          componentSpareParts: spareParts.componentSpareParts,
          maintenanceJobSpareParts: spareParts.maintenanceJobSpareParts,
          storageSpareParts: spareParts.storageSpareParts,
        });
      })
    );
  }

  @Action(LoadMaintenanceJobById, { cancelUncompleted: true })
  loadMaintenanceJobById(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { id }: LoadMaintenanceJobById
  ) {
    return this.service.loadMaintenanceJobById(id).pipe(
      tap((maintenanceJob: IMaintenanceJob) => {
        setState(
          patch({
            currentMaintenanceJob: maintenanceJob,
          })
        );
      })
    );
  }

  @Action(LoadCorrectiveActionById, { cancelUncompleted: true })
  loadCorrectiveActionById(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { id }: LoadCorrectiveActionById
  ) {
    return this.correctiveActionApiService.getCorrectiveActionById(id).pipe(
      tap((correctiveAction) => {
        setState(
          patch({
            currentMaintenanceJob: mapCorrectiveActionToMaintenanceJob(correctiveAction),
          })
        );
      })
    );
  }

  @Action(LoadCorrectiveActionPerformById, { cancelUncompleted: true })
  loadCorrectiveActionPerformById(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { performId, isVersion }: LoadCorrectiveActionPerformById
  ) {
    return this.correctiveActionApiService.loadCorrectiveActionPerformById(performId).pipe(
      tap((maintenanceJob: IMaintenanceJob) => {
        setState(
          patch({
            [isVersion ? 'currentMaintenanceJobVersion' : 'currentMaintenanceJob']: maintenanceJob,
          })
        );
      })
    );
  }

  @Action(LoadMaintenanceJobVersionById, { cancelUncompleted: true })
  loadMaintenanceJobVersionById(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { id, versionId }: LoadMaintenanceJobVersionById
  ) {
    return this.service.loadMaintenanceJobVersionById(id, versionId).pipe(
      tap((maintenanceJob: IMaintenanceJob) => {
        setState(
          patch({
            currentMaintenanceJobVersion: maintenanceJob,
          })
        );
      })
    );
  }

  @Action(LoadMaintenanceJobsForIO, { cancelUncompleted: true })
  loadMaintenanceJobsForIO({ setState }: StateContext<MaintenanceJobStateModel>) {
    return this.service.loadMaintenanceJobsForIO().pipe(
      tap((jobs: IdName[]) => {
        setState(
          patch({
            jobsForIO: jobs,
          })
        );
      })
    );
  }

  @Action(CreateMaintenanceJob)
  @OnSuccess({ message: 'maintenanceJobAdded' })
  createMaintenanceJob(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { maintenanceJob }: CreateMaintenanceJob
  ) {
    return this.service.createMaintenanceJob(maintenanceJob);
  }

  @Action(UpdateMaintenanceJob)
  @OnSuccess({ message: 'maintenanceJobUpdated' })
  updateMaintenanceJob(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { maintenanceJob }: UpdateMaintenanceJob
  ) {
    return this.service.updateMaintenanceJob(maintenanceJob);
  }

  @Action(PostponeMaintenanceJob)
  @OnSuccess({ message: 'maintenanceJobPostponed' })
  postponeMaintenanceJob(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { request }: PostponeMaintenanceJob
  ) {
    return this.service.postponeMaintenanceJob(request);
  }

  @Action(GetPerformMaintenanceJobDetails)
  getPerformMaintenanceJobDetails(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { performId }: GetPerformMaintenanceJobDetails
  ) {
    return this.service.getPerformMaintenanceJobDetails(performId).pipe(
      tap((details: IPerformJobDetails) => {
        setState(
          patch({
            currentPerformDetails: details,
          })
        );
      })
    );
  }

  @Action(GetCorrectiveActionPerformDetails)
  getCorrectiveActionPerformDetails(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { performId }: GetCorrectiveActionPerformDetails
  ) {
    return this.correctiveActionApiService.getCorrectiveActionPerformDetails(performId).pipe(
      tap((details: IPerformJobDetails) => {
        setState(
          patch({
            currentPerformDetails: details,
          })
        );
      })
    );
  }

  @Action(PerformMaintenanceJob)
  performMaintenanceJob(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { request, isDialog, isCorrectiveAction }: PerformMaintenanceJob
  ) {
    const performRequest = isCorrectiveAction
      ? this.correctiveActionApiService.performCorrectiveAction(request)
      : this.service.performMaintenanceJob(request);

    return performRequest.pipe(
      tap(({ performJobId }) => {
        this.toast.success(
          isCorrectiveAction ? 'correctiveActionPerformed' : 'maintenanceJobPerformed'
        );
        setState(
          patch({
            lastPerformId: performJobId,
          })
        );
        const isFromDueList =
          this.store.selectSnapshot(DueListState.getLastDueListItem)?.id === request.id;
        if (isFromDueList) {
          this.store.dispatch(new ClearLastDueListItem());
          redirectAndBypassUnsavedDataCheck(DueListRoutes.Main);
          return;
        }
        if (!isDialog && !isFromDueList) {
          const returnTarget = getMaintenanceComponentReturnTarget(
            this.currentComponentId,
            this.router.parseUrl(this.router.url).queryParams
          );
          const redirectUrl = isCorrectiveAction
            ? returnTarget.url
            : getLinkToMaintenanceJobDetails(this.currentComponentId, request.id);
          redirectAndBypassUnsavedDataCheck(
            redirectUrl,
            isCorrectiveAction
              ? { queryParams: returnTarget.queryParams }
              : {
                  queryParams: { referrer: null, draft: null },
                  queryParamsHandling: 'merge',
                }
          );
        }
      })
    );
  }

  @Action(BulkPerformMaintenanceJobs)
  bulkPerformMaintenanceJobs(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { request }: BulkPerformMaintenanceJobs
  ) {
    return this.service.performMaintenanceJobsBulk(request).pipe(
      tap((result) => {
        setState(
          patch({
            lastBulkPerformResult: result,
          })
        );
      })
    );
  }

  @Action(LoadMaintenanceJobLog)
  loadMaintenanceJobLog(
    { setState, getState }: StateContext<MaintenanceJobStateModel>,
    { id }: LoadMaintenanceJobLog
  ) {
    // for some reason the same thing doesn't work with setState(patch({currentMaintenanceJobLog: undefined}))
    setState({
      ...getState(),
      currentMaintenanceJobLog: undefined,
    });
    return this.service.getMaintenanceJobLog(id).pipe(
      tap((log) => {
        setState(
          patch({
            currentMaintenanceJobLog: log.records,
          })
        );
      })
    );
  }

  @Action(LoadRecentMaintenanceJobPerformLogs)
  loadRecentMaintenanceJobPerformLogs(
    { setState, getState }: StateContext<MaintenanceJobStateModel>,
    { id }: LoadRecentMaintenanceJobPerformLogs
  ) {
    setState({
      ...getState(),
      recentPerformLogs: undefined,
    });
    return this.service.getRecentPerformLogs(id).pipe(
      tap((log) => {
        setState(
          patch({
            recentPerformLogs: log.records,
          })
        );
      })
    );
  }

  @Action(DeleteMaintenanceJob, { cancelUncompleted: true })
  @OnSuccess({ message: 'maintenanceJobDeleted' })
  deleteMaintenanceJob(_ctx: StateContext<MaintenanceJobStateModel>, { id }: DeleteMaintenanceJob) {
    return this.service.deleteMaintenanceJob(id);
  }

  @Action(RestoreMaintenanceJob, { cancelUncompleted: true })
  @OnSuccess({ message: 'maintenanceJobRestored' })
  restoreMaintenanceJob(
    { setState, getState }: StateContext<MaintenanceJobStateModel>,
    { id }: RestoreMaintenanceJob
  ) {
    return this.service.restoreMaintenanceJob(id).pipe(
      tap(() => {
        setState(
          patch({
            groupedDeletedJobs: getState().groupedDeletedJobs?.filter((group) =>
              group.itemsOnNode.some((item) => item.jobId !== id)
            ),
          })
        );
      })
    );
  }

  @Action(PrintMaintenanceJobs, { cancelUncompleted: true })
  printMaintenanceJob(
    { setState }: StateContext<MaintenanceJobStateModel>,
    { request, filter }: PrintMaintenanceJobs
  ) {
    return this.service.printMaintenanceJobs(request, filter).pipe(
      tap((file) => {
        setState(patch({ currentPrintedJobs: file }));
      })
    );
  }

  @Action(LoadJobDraftPerform, { cancelUncompleted: true })
  loadTaskDraftPerform(
    { getState, setState }: StateContext<MaintenanceJobStateModel>,
    { id, isCorrectiveAction }: LoadJobDraftPerform
  ) {
    const request = isCorrectiveAction
      ? this.correctiveActionApiService.loadCorrectiveActionDraftPerform(id)
      : this.service.loadJobDraftPerform(id);

    return request.pipe(
      tap((draftPerform) => {
        setState({ ...getState(), draftPerform });
      })
    );
  }
  @Action(SaveJobDraftPerform, { cancelUncompleted: true })
  @OnSuccess({ message: 'draftSaved' })
  saveTaskDraftPerform(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { job, isDialog, isCorrectiveAction }: SaveJobDraftPerform
  ) {
    const saveRequest = isCorrectiveAction
      ? this.correctiveActionApiService.saveCorrectiveActionDraftPerform(job)
      : this.service.saveJobDraftPerform(job);

    return saveRequest.pipe(
      tap(() => {
        if (!isDialog) {
          const redirectUrl = isCorrectiveAction
            ? getLinkToCorrectiveActionEdit(this.currentComponentId, job.id)
            : getLinkToMaintenanceJobDetails(this.currentComponentId, job.id);
          redirectAndBypassUnsavedDataCheck(redirectUrl, {
            queryParams: { referrer: null, draft: null },
            queryParamsHandling: 'merge',
          });
        }
      })
    );
  }

  @Action(PrintMaintenanceJobsReport, { cancelUncompleted: true })
  printMaintenanceJobsReport(
    { getState, setState }: StateContext<MaintenanceJobStateModel>,
    { filter, menuId }: PrintMaintenanceJobsReport
  ) {
    return this.service.printReport(filter, menuId).pipe(
      tap((file: Blob) => {
        const state = getState();
        setState({
          ...state,
          maintenanceJobsReport: file,
        });
      })
    );
  }

  @Action(DeactivateMaintenanceJob, { cancelUncompleted: true })
  @OnSuccess({ message: 'maintenanceJobDeactivated' })
  deactivateMaintenanceJob(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { id }: DeactivateMaintenanceJob
  ) {
    return this.service.deactivateMaintenanceJob(id);
  }

  @Action(ReactivateMaintenanceJob, { cancelUncompleted: true })
  @OnSuccess({ message: 'maintenanceJobReactivated' })
  reactivateMaintenanceJob(
    _ctx: StateContext<MaintenanceJobStateModel>,
    { id }: ReactivateMaintenanceJob
  ) {
    return this.service.reactivateMaintenanceJob(id);
  }

  @Action(ClearMaintenanceJobsByMenu, { cancelUncompleted: true })
  clearMaintenanceJobsByMenu(ctx: StateContext<MaintenanceJobStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      groupedJobs: [],
      groupedDeletedJobs: [],
      groupedCompletedJobs: [],
      maintenanceJobsTotalPages: 0,
      deletedJobsTotalPages: 0,
      completedJobsTotalPages: 0,
    });
  }

  @Action(ClearCurrentMaintenanceJob)
  clearMaintenanceJob(ctx: StateContext<MaintenanceJobStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentMaintenanceJob: undefined,
    });
  }

  @Action(LoadMaintenanceJobStatus, { cancelUncompleted: true })
  loadMaintenanceJobStatus(
    { getState, setState }: StateContext<MaintenanceJobStateModel>,
    { departmentIds, from, to }: LoadMaintenanceJobStatus
  ) {
    return this.service.getMaintenanceJobStatus(departmentIds, from, to).pipe(
      tap((maintenanceJobStatus: IMaintenanceJobStatusResponse) => {
        const state = getState();
        setState({
          ...state,
          maintenanceJobStatus,
        });
      })
    );
  }

  @Selector()
  static getMaintenanceJobsTotalPages(state: MaintenanceJobStateModel) {
    return state.maintenanceJobsTotalPages;
  }

  @Selector()
  static getDeletedJobsTotalPages(state: MaintenanceJobStateModel) {
    return state.deletedJobsTotalPages;
  }

  @Selector()
  static getDeactivatedJobsTotalPages(state: MaintenanceJobStateModel) {
    return state.deactivatedJobsTotalPages;
  }

  @Selector()
  static getComponentSpareParts(state: MaintenanceJobStateModel) {
    return state.componentSpareParts;
  }

  @Selector()
  static getMaintenanceJobSpareParts(state: MaintenanceJobStateModel) {
    return state.maintenanceJobSpareParts;
  }

  @Selector()
  static getStorageSpareParts(state: MaintenanceJobStateModel) {
    return state.storageSpareParts;
  }

  @Selector()
  static groupedJobs(state: MaintenanceJobStateModel) {
    return state.groupedJobs;
  }

  @Selector()
  static groupedDeletedJobs(state: MaintenanceJobStateModel) {
    return state.groupedDeletedJobs;
  }

  @Selector()
  static groupedDeactivatedJobs(state: MaintenanceJobStateModel) {
    return state.groupedDeactivatedJobs;
  }

  @Selector()
  static groupedCompletedJobs(state: MaintenanceJobStateModel) {
    return state.groupedCompletedJobs;
  }

  @Selector()
  static getCompletedJobsTotalPages(state: MaintenanceJobStateModel) {
    return state.completedJobsTotalPages;
  }

  @Selector()
  static getCurrentMaintenanceJob(state: MaintenanceJobStateModel) {
    return state.currentMaintenanceJob;
  }

  @Selector()
  static getCurrentMaintenanceJobVersion(state: MaintenanceJobStateModel) {
    return state.currentMaintenanceJobVersion;
  }

  @Selector()
  static getCurrentPerformDetails(state: MaintenanceJobStateModel) {
    return state.currentPerformDetails;
  }

  @Selector()
  static getCurrentMaintenanceJobLog(state: MaintenanceJobStateModel) {
    return state.currentMaintenanceJobLog;
  }

  @Selector()
  static getRecentPerformLogs(state: MaintenanceJobStateModel) {
    return state.recentPerformLogs;
  }

  @Selector()
  static getCurrentPrintedJobs(state: MaintenanceJobStateModel) {
    return state.currentPrintedJobs;
  }

  @Selector()
  static getDraftPerform(state: MaintenanceJobStateModel) {
    return state.draftPerform;
  }

  @Selector()
  static getJobsForIO(state: MaintenanceJobStateModel) {
    return state.jobsForIO;
  }

  @Selector()
  static getLastPerformId(state: MaintenanceJobStateModel) {
    return state.lastPerformId;
  }

  @Selector()
  static getLastBulkPerformResult(state: MaintenanceJobStateModel) {
    return state.lastBulkPerformResult;
  }

  @Selector()
  static getMaintenanceJobReport(state: MaintenanceJobStateModel) {
    return state.maintenanceJobsReport;
  }

  @Selector()
  static getMaintenanceJobStatus(state: MaintenanceJobStateModel) {
    return state.maintenanceJobStatus;
  }
}
